import type {
  Base64FileInput,
  QuickEntriesJobFile,
} from '@/types/gql.generated';

// Shared upload size limit. Kept here (rather than in the magic feature) so
// every upload entry point across the app can enforce the same cap.
export const MAX_FILE_SIZE_MB = 10; // MB
const MB = 1024 * 1024;

export const OVERSIZED_FILE_MESSAGE = `This file is too big. Please try a file smaller than ${MAX_FILE_SIZE_MB}MB.`;

/**
 * True when a user-selected file is within the upload size limit. Server-side
 * representations of already-uploaded files carry no browser `size`, so they
 * are always treated as valid.
 */
export const isFileValid = (
  file: File | QuickEntriesJobFile | null
): boolean => {
  if (!file) {
    return false;
  }

  if (file instanceof File) {
    return file.size < MAX_FILE_SIZE_MB * MB;
  }

  return true;
};

/**
 * Builds a real `Error` for a failed `FileReader` read.
 *
 * `FileReader` hands its `onerror` handler a `ProgressEvent`, never an `Error`.
 * Passing that straight to a promise's `reject` rejects with a value that has no
 * message and no stack, so when it escapes to the global unhandledrejection
 * handler Sentry can only file it as "ProgressEvent (type=error) captured as
 * promise rejection" — a single opaque bucket with no route back to the call
 * site, and the largest one in the project (Sentry WEB-CLIENT-Z6).
 *
 * The actionable detail is on `reader.error`, a DOMException whose `name`
 * separates the real failure modes: `NotReadableError` (the OS refused the read
 * — the common iOS Safari case, where a picked file is evicted or its
 * security-scoped handle expires before it is read), `NotFoundError` (the file
 * moved between picking and reading), `SecurityError`, and `AbortError`. Putting
 * that name in the message gives Sentry something to group by; the DOMException
 * itself is kept as `cause` for the detail.
 */
export const toFileReadError = (reader: FileReader): Error => {
  const { error } = reader;
  const detail = error
    ? `${error.name}: ${error.message}`
    : 'no error on the reader';
  return new Error(`Failed to read file (${detail})`, { cause: error });
};

export const toBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onerror = () => reject(toFileReadError(reader));
    reader.onload = () => {
      if (!reader.result) {
        return reject(new Error('Failed to read file, result is null'));
      }

      const str = reader.result.toString();
      if (!str) {
        return reject(new Error('Failed to read file, result is empty'));
      }

      resolve(str);
    };
  });
};

export const toBase64FileInput = async (
  file: File
): Promise<Base64FileInput> => {
  const data = await toBase64(file);
  const mimetype = file.type;

  return {
    mimetype,
    filename: file.name,
    data: data.replace(`data:${mimetype};base64,`, ''),
  };
};

/** Is `file` a representation of a user-selected PDF file blob? */
export const isPdfFile = (file: unknown): file is File => {
  return file instanceof File && file.type === 'application/pdf';
};

/** Is `file` a server representation of an uploaded PDF? */
export const isPdfFromServer = (file: unknown): boolean => {
  return (
    typeof file === 'object' &&
    file !== null &&
    'mimetype' in file &&
    file.mimetype === 'application/pdf'
  );
};
